/*Copyright (c) 2023. Honeywell International, Inc. All rights reserved.
This file contains trade secrets of Honeywell International, Inc. 
No part may be reproduced or transmitted in any form by any means or for any purpose without the express written permission of Honeywell. */

import { Tree, TreeCheckboxSelectionKeys } from "primereact/tree";
import { TreeNode } from "primereact/treenode";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import "./GroupHierarchy.scss";

interface SelectedNodes {
  [key: string]: {
    checked: boolean;
    partialChecked: boolean;
    label: string;
  };
}
interface Node {
  key: string;
  label: string;
  children?: Node[];
}

type SelectedList = {
  [key: string]: {
    checked: boolean;
    partialChecked: boolean;
  };
};
export const GroupHierarchy = (props: any) => {
  const [selectedKeys, setSelectedKeys] =
    useState<TreeCheckboxSelectionKeys | null>(null);
  const [selectedKeysAndLabels, setSelectedKeysAndLabels] =
    useState<SelectedNodes>({});
  const [nodes, setNodes] = useState<TreeNode[]>([]);
  const currentUserData: any = useSelector(
    (state: RootState) => state.organisation.getCurrentUserData
  );
  useEffect(() => {
    props.comp === "tenantUserPage"
      ? setNodes(props.hierarchicalOrganizationwKey)
      : filterByText(
          props.hierarchicalOrganizationwKey,
          currentUserData.organization.map((el: any) => el.name)
        );
  }, [props.hierarchicalOrganizationwKey, currentUserData]);

  useEffect(() => {
    const labels = Object.keys(selectedKeysAndLabels)
      .filter((key) => selectedKeysAndLabels[key].checked === true)
      .map((key) => selectedKeysAndLabels[key].label);
    props.groupFilter(labels);
  }, [selectedKeysAndLabels]);

  useEffect(() => {
    if (props.selectedKeyLsit) {
      const selectedList = findKeysByLabels(nodes, props.selectedKeyLsit);
      const selectedNodes = getSelectedNodes(nodes, selectedList);
      setSelectedKeysAndLabels({ ...selectedKeysAndLabels, ...selectedNodes });
      setSelectedKeys(selectedList);
    }
  }, [props.selectedKeyLsit, nodes]);
  useEffect(() => {
    if (props.clearFilter) {
      setSelectedKeys({});
    }
  }, [props.clearFilter]);

  function findKeysByLabels(data: any, labels: string[]) {
    let keys: any = {};
    function searchLabels(node: any) {
      if (node.children) {
        node.children.forEach((child: any) => searchLabels(child));
      }
      if (labels.includes(node.label)) {
        keys[node.key] = { checked: true, partialChecked: false };
      }
    }
    data.forEach((item: any) => searchLabels(item));
    return keys;
  }

  // FilterBy text
  // istanbul ignore next
  // Takes objects and texts as input
  function filterByText(objects: any, texts: any) {
    let keyObj: string[] = [];
    function traverse(arr: any, texts: any) {
      const arrObj = [];

      for (const item of arr) {
        const matchingChildren: any = item.children
          ? traverse(item.children, texts)
          : [];
        if (
          texts.includes(item.label) ||
          (item.data && texts.includes(item.label))
        ) {
          arrObj.push({
            ...item,
            selectable: texts.includes(item.label),
            children: matchingChildren,
          });
          keyObj.push(item.key);
        } else if (matchingChildren.length > 0) {
          arrObj.push({
            ...item,
            selectable: texts.includes(item.label),
            children: matchingChildren,
          });
          keyObj.push(item.key);
        }
      }

      return arrObj;
    }

    const filteredObj = traverse(objects, texts);
    if (filteredObj.length) {
      setNodes(filteredObj);
    }
  }

  // const extractDataFromKeys = (): SelectedList => {
  //   const selectedList: SelectedList = {};
  //   const traverse = (node: Node | undefined) => {
  //     if (!node) return;

  //     if (props.selectedKeyLsit.includes(node.label)) {
  //       selectedList[node.key] = {
  //         checked: true,
  //         partialChecked: false,
  //       };
  //     } else if (node.children) {
  //       let childChecked = 0;
  //       let childPartialChecked = 0;

  //       node.children.forEach((child) => {
  //         traverse(child);
  //         if (selectedList[child.key]?.checked) {
  //           childChecked++;
  //         } else if (selectedList[child.key]?.partialChecked) {
  //           childPartialChecked++;
  //         }
  //       });

  //       if (childChecked > 0 || childPartialChecked > 0) {
  //         selectedList[node.key] = {
  //           checked: false,
  //           partialChecked: true,
  //         };
  //       } else {
  //         selectedList[node.key] = {
  //           checked: false,
  //           partialChecked: false,
  //         };
  //       }
  //     }
  //   };
  //   nodes.forEach((node: any) => traverse(node));
  //   return selectedList;
  // };

  // Find label by Key
  // istanbul ignore next
  // Takes tree and targetKey as input
  const findLabelByKey = (tree: any[], targetKey: string): string | null => {
    if (!tree || tree.length === 0) return null;
    for (const node of tree) {
      if (node.key === targetKey) {
        return node.label;
      }
      if (node.children) {
        const label = findLabelByKey(node.children, targetKey);
        if (label) {
          return label;
        }
      }
    }
    return null;
  };

  const getSelectedNodes = (
    treeNodes: TreeNode[],
    selectionValues: any
  ): SelectedNodes => {
    const selectedNodes: SelectedNodes = {};
    Object.keys(selectionValues).forEach((key) => {
      const label: any = findLabelByKey(treeNodes, key);
      if (label) {
        selectedNodes[key] = { ...selectionValues[key], label };
      }
    });
    return selectedNodes;
  };

  // onSelect of dropdown
  // istanbul ignore next
  // Takes event and checked as input
  const onSelect = (e: any, checked: boolean) => {
    const obj = {
      value: {
        [e.node.key]: { checked: checked, partialChecked: false },
      },
    };
    const selectedNodes = getSelectedNodes(nodes, {
      ...selectedKeys,
      ...obj.value,
    });
    setSelectedKeysAndLabels({ ...selectedKeysAndLabels, ...selectedNodes });
    setSelectedKeys({ ...selectedKeys, ...obj.value });
  };
  return (
    <div className="GroupHierarchy">
      <Tree
        value={nodes}
        filter
        filterMode="strict"
        filterPlaceholder="Search"
        className="w-full sm:w-10rem"
        selectionMode="checkbox"
        selectionKeys={selectedKeys}
        onSelect={(e: any) => onSelect(e, true)}
        onUnselect={(e: any) => onSelect(e, false)}
        data-testid="treeHierarchy"
      />
    </div>
  );
};

export default GroupHierarchy;
